/**
 * Chat Page
 * AI-powered tutoring chat interface for children
 * Integrates with the chat service and handles moderation
 * Supports voice input (speech-to-text) and audio playback (text-to-speech)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Sparkles, ArrowLeft, AlertCircle, RefreshCw, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SUBJECT_DISPLAY_NAMES, SUBJECT_ICONS } from '@kids-lms/constants';
import type { Subject } from '@kids-lms/types';
import { useChatStore, useChildStore, useSoundEffects, useHaptic, useTouchFeedback, type ChatMessage } from '../hooks';
import { SoundEngine } from '../audio';
import { getErrorMessage, ttsService } from '../services';
import MindoraCharacter from '../components/character/MindoraCharacter';
import Confetti from '../components/effects/Confetti';
import { SparkleCluster } from '../components/decorations';

// Speech recognition types
interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition: new () => SpeechRecognitionInstance;
  }
}

export default function ChatPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subject = searchParams.get('subject') as Subject | null;

  // Store state
  const {
    messages,
    isSending,
    error,
    sendMessage,
    startNewConversation,
    setError,
  } = useChatStore();

  const { selectedChild } = useChildStore();

  // Local state
  const [input, setInput] = useState('');
  const [showWelcome, setShowWelcome] = useState(true);
  const [messageCount, setMessageCount] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [firstSent, setFirstSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Voice state
  const [isListening, setIsListening] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  const [speakingMsgId, setSpeakingMsgId] = useState<string | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastSpokenMsgIdRef = useRef<string | null>(null);
  const voiceTranscriptRef = useRef('');
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  // Sound effects
  const { play } = useSoundEffects();
  const haptic = useHaptic();
  const touchFeedback = useTouchFeedback();

  // Check browser support
  const hasSpeechRecognition = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Speech-to-text: auto-stop mode for minimal interaction
  const startListening = useCallback(async () => {
    if (!hasSpeechRecognition) return;
    setMicError(null);

    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (e) { /* ignore */ }
      recognitionRef.current = null;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
    } catch {
      setMicError('Please allow microphone access');
      setTimeout(() => setMicError(null), 3000);
      return;
    }

    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;

      recognition.onstart = () => {
        setIsListening(true);
        voiceTranscriptRef.current = '';
        play('toggle');
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
        const transcript = finalTranscript || interimTranscript;
        setInput(transcript);
        voiceTranscriptRef.current = transcript;
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        if (event.error === 'not-allowed') {
          setMicError('Microphone blocked by browser');
          setTimeout(() => setMicError(null), 3000);
        } else if (event.error === 'network') {
          setMicError('Network error - check connection');
          setTimeout(() => setMicError(null), 3000);
        }
        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          voiceTranscriptRef.current = '';
        }
        setIsListening(false);
        recognitionRef.current = null;
      };

      recognition.onend = () => {
        setIsListening(false);
        recognitionRef.current = null;
        play('toggle');
        const transcript = voiceTranscriptRef.current.trim();
        if (transcript) {
          voiceTranscriptRef.current = '';
          setInput('');
          setShowWelcome(false);
          setTimeout(() => {
            sendMessageRef.current(transcript);
          }, 50);
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch {
      setMicError('Speech recognition failed');
      setTimeout(() => setMicError(null), 3000);
      setIsListening(false);
    }
  }, [hasSpeechRecognition]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
    }
  }, []);

  // Text-to-speech via backend OpenAI TTS
  const speakText = useCallback(async (text: string, msgId: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setSpeakingMsgId(msgId);
    SoundEngine.getInstance().duckAmbient();
    try {
      const audio = await ttsService.speak(text);
      audioRef.current = audio;
      audio.addEventListener('ended', () => {
        setSpeakingMsgId(null);
        audioRef.current = null;
        SoundEngine.getInstance().unduckAmbient();
      });
      audio.addEventListener('error', () => {
        setSpeakingMsgId(null);
        audioRef.current = null;
        SoundEngine.getInstance().unduckAmbient();
      });
    } catch {
      setSpeakingMsgId(null);
      SoundEngine.getInstance().unduckAmbient();
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeakingMsgId(null);
    SoundEngine.getInstance().unduckAmbient();
  }, []);

  // Auto-speak new AI responses
  useEffect(() => {
    if (!autoSpeak) return;

    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.isLoading && lastMsg.content) {
      if (lastMsg.id !== lastSpokenMsgIdRef.current) {
        lastSpokenMsgIdRef.current = lastMsg.id;
        speakText(lastMsg.content, lastMsg.id);
      }
    }
  }, [messages, autoSpeak, speakText]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.abort();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Track message count for milestone confetti
  useEffect(() => {
    const userMessages = messages.filter(m => m.role === 'user').length;
    if (userMessages > 0 && userMessages % 5 === 0 && userMessages !== messageCount) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 100);
    }
    setMessageCount(userMessages);
  }, [messages, messageCount]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (selectedChild) {
      startNewConversation(selectedChild.id, subject || undefined);
    }
  }, [selectedChild, subject, startNewConversation]);

  useEffect(() => {
    if (!selectedChild) {
      navigate('/learn');
    }
  }, [selectedChild, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isSending) return;

    const messageContent = input.trim();
    setInput('');
    setShowWelcome(false);

    if (!firstSent) setFirstSent(true);

    const response = await sendMessage(messageContent);

    if (response?.blocked) {
      setError(null);
    }

    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleRetry = () => {
    setError(null);
    inputRef.current?.focus();
  };

  const getWelcomeMessage = (): string => {
    const childName = selectedChild?.name || 'there';

    if (subject) {
      return `Hi ${childName}! I'm Mindora, your learning buddy! ${SUBJECT_ICONS[subject]} I'm excited to help you learn about ${SUBJECT_DISPLAY_NAMES[subject]}! What would you like to know?`;
    }

    return `Hello ${childName}! I'm Mindora, your AI learning buddy! I can help you with math, science, English, history, and lots more! What would you like to learn about today?`;
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.role === 'user';

    return (
      <motion.div
        key={message.id}
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          type: 'spring',
          stiffness: 280,
          damping: 20,
          delay: index * 0.03,
        }}
        className={`flex items-end gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}
      >
        {/* Mascot avatar for assistant messages */}
        {!isUser && (
          <div className="flex-shrink-0 mb-1">
            <MindoraCharacter
              mood={message.isLoading ? 'thinking' : 'idle'}
              size="sm"
              animate={message.isLoading}
            />
          </div>
        )}

        <div
          className={
            isUser
              ? 'chat-bubble-user'
              : `chat-bubble-assistant ${message.isLoading ? '' : ''}`
          }
        >
          {message.isLoading ? (
            <div className="typing-indicator" aria-label="Mindora is thinking...">
              <span></span>
              <span></span>
              <span></span>
            </div>
          ) : (
            <div>
              <p className="whitespace-pre-wrap text-kid-base">{message.content}</p>
              {!isUser && message.content && (
                <div className="flex justify-end mt-1.5">
                  <button
                    onClick={() => speakingMsgId === message.id ? stopSpeaking() : speakText(message.content, message.id)}
                    onPointerDown={touchFeedback.onPointerDown}
                    className={`inline-flex items-center gap-1 px-4 py-2.5 rounded-kid-sm text-kid-sm font-bold transition-colors touch-kid touch-glow touch-slop ${
                      speakingMsgId === message.id
                        ? 'bg-kid-ocean/10 text-kid-ocean'
                        : 'text-kid-text-soft hover:text-kid-ocean hover:bg-kid-ocean/5'
                    }`}
                    aria-label={speakingMsgId === message.id ? 'Stop reading' : 'Read aloud'}
                  >
                    {speakingMsgId === message.id ? (
                      <><VolumeX className="w-5 h-5" strokeWidth={2.25} /> Stop</>
                    ) : (
                      <><Volume2 className="w-5 h-5" strokeWidth={2.25} /> Listen</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Child avatar for user messages */}
        {isUser && (
          <div className="flex-shrink-0 mb-1">
            <img
              src="/avatar-boy.png"
              alt="You"
              className="w-8 h-8 rounded-full object-cover border-2 border-kid-violet/30"
            />
          </div>
        )}
      </motion.div>
    );
  };

  if (!selectedChild) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)] bg-kid-bg">
        <div className="text-center p-8">
          <MindoraCharacter mood="waiting" size="lg" />
          <h2 className="text-kid-xl font-display text-kid-text mt-4 mb-2">No Profile Selected</h2>
          <p className="text-kid-text-soft text-kid-base mb-4">Please select a child profile to start learning.</p>
          <Link to="/learn" className="btn-kid-ocean">
            Go Back
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Confetti for milestones */}
      <Confetti trigger={showConfetti} count={20} duration={2500} />

      {/* Header — warm gradient */}
      <header className="flex items-center gap-4 p-4 bg-gradient-to-r from-kid-ocean/10 via-kid-violet/10 to-kid-berry/10 border-b-2 border-kid-ocean/15 relative">
        <Link
          to="/learn"
          onPointerDown={touchFeedback.onPointerDown}
          className="flex flex-col items-center gap-0.5 p-3 hover:bg-white/60 rounded-kid-sm transition-colors touch-kid touch-glow touch-slop"
          aria-label="Go back to subjects"
        >
          <ArrowLeft className="w-6 h-6 text-kid-text" strokeWidth={2.25} />
          <span className="text-xs font-bold text-kid-text-soft leading-tight">Back</span>
        </Link>
        {/* Sparkle cluster near header */}
        <SparkleCluster count={3} spread={30} className="absolute top-2 right-4" />
        <div className="flex items-center gap-3">
          <MindoraCharacter mood="idle" size="sm" />
          <div>
            <h1 className="font-display text-kid-lg text-kid-text">
              {subject ? SUBJECT_DISPLAY_NAMES[subject] : 'Ask Me Anything'}
            </h1>
            <p className="text-kid-sm text-kid-text-soft">
              Learning with {selectedChild.name}
            </p>
          </div>
        </div>
        {subject && (
          <div className="ml-auto p-2 bg-white/60 rounded-kid-sm">
            <span className="text-2xl" role="img" aria-label={subject}>
              {SUBJECT_ICONS[subject]}
            </span>
          </div>
        )}
      </header>

      {/* Messages Area */}
      <main
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-kid-bg texture-paper-grain kid-scrollbar overscroll-bouncy"
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        {/* Welcome Message */}
        {showWelcome && messages.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-8"
          >
            <div className="relative">
              <MindoraCharacter mood="waving" size="xl" />
              {/* Floating sparkles */}
              {[0, 1, 2, 3].map((i) => (
                <motion.span
                  key={i}
                  className="absolute text-kid-dandelion pointer-events-none"
                  style={{
                    top: `${15 + i * 20}%`,
                    left: i % 2 === 0 ? '-20%' : '100%',
                    fontSize: '16px',
                  }}
                  animate={{
                    opacity: [0, 1, 0],
                    scale: [0.5, 1, 0.5],
                    y: [0, -10, 0],
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 2,
                    delay: i * 0.5,
                  }}
                >
                  ✨
                </motion.span>
              ))}
            </div>
            <div className="mt-4 bg-white rounded-kid p-5 max-w-md text-center border-2 border-kid-ocean/20 shadow-kid">
              <p className="whitespace-pre-wrap text-kid-text text-kid-base">{getWelcomeMessage()}</p>
            </div>
          </motion.div>
        )}

        {/* Chat Messages */}
        <AnimatePresence initial={false}>
          {messages.map((message, index) => renderMessage(message, index))}
        </AnimatePresence>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center"
          >
            <div className="bg-kid-coral/10 border-2 border-kid-coral/20 rounded-kid p-4 max-w-md">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-kid-coral flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                <div className="flex-1">
                  <p className="text-kid-coral text-kid-sm font-bold">{getErrorMessage(error)}</p>
                  <button
                    onClick={handleRetry}
                    onPointerDown={touchFeedback.onPointerDown}
                    className="mt-2 px-4 py-2.5 rounded-kid-sm text-kid-sm text-kid-coral hover:text-kid-berry flex items-center gap-1 font-bold touch-kid touch-glow touch-slop"
                  >
                    <RefreshCw className="w-5 h-5" strokeWidth={2.25} />
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </main>

      {/* Input Area — frosted glass */}
      <footer className="p-4 bg-white/80 backdrop-blur-md border-t-2 border-kid-ocean/10">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="flex gap-2 items-center">
            {/* Mic Button */}
            {hasSpeechRecognition && (
              <div className="relative flex-shrink-0">
                <button
                  type="button"
                  onClick={isListening ? stopListening : startListening}
                  onPointerDown={() => haptic.tap()}
                  disabled={isSending}
                  className={`btn-squishy p-3.5 rounded-kid-sm transition-all touch-kid touch-glow ${
                    micError
                      ? 'bg-kid-dandelion text-white'
                      : isListening
                        ? 'bg-kid-coral text-white animate-pulse shadow-lg shadow-kid-coral/30'
                        : 'bg-kid-bg text-kid-text-soft hover:bg-kid-ocean/10 hover:text-kid-ocean border-2 border-kid-ocean/15'
                  }`}
                  aria-label={isListening ? 'Stop listening' : 'Start voice input'}
                  title={micError || (isListening ? 'Tap to stop' : 'Tap to speak')}
                >
                  {isListening ? <MicOff className="w-6 h-6" strokeWidth={2.25} /> : <Mic className="w-6 h-6" strokeWidth={2.25} />}
                  <span className="text-xs font-bold leading-tight">{isListening ? 'Mute' : 'Talk'}</span>
                </button>
                {micError && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-kid-coral text-white text-xs rounded-kid-sm whitespace-nowrap shadow-lg font-bold">
                    {micError}
                  </div>
                )}
              </div>
            )}

            {/* Text Input */}
            <div className="flex-1 relative">
              <label htmlFor="chat-input" className="sr-only">
                Type or speak your question
              </label>
              <textarea
                id="chat-input"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening ? 'Listening... speak now!' : 'Type or tap mic to speak...'}
                className="w-full px-4 py-3.5 pr-14 rounded-kid-sm border-2 border-kid-ocean/15 bg-kid-bg focus:border-kid-ocean focus:ring-2 focus:ring-kid-ocean/20 focus:outline-none transition-all duration-200 resize-none min-h-[52px] max-h-32 text-kid-base text-kid-text placeholder:text-kid-text-soft/50"
                rows={1}
                disabled={isSending}
                maxLength={1000}
              />
              {input.length > 800 && (
                <span className={`absolute right-16 bottom-1 text-xs font-bold ${
                  input.length > 950 ? 'text-kid-coral' : 'text-kid-text-soft/60'
                }`}>
                  {input.length}/1000
                </span>
              )}
              <button
                type="submit"
                disabled={!input.trim() || isSending}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-3.5 rounded-kid-full text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all focus:outline-none focus:ring-2 focus:ring-kid-violet/50 btn-squishy touch-kid touch-glow"
                style={{
                  background: 'linear-gradient(135deg, var(--kid-violet), var(--kid-ocean))',
                }}
                aria-label="Send message"
              >
                {isSending ? (
                  <RefreshCw className="w-6 h-6 animate-spin" strokeWidth={2.25} />
                ) : (
                  <Send className="w-6 h-6" strokeWidth={2.25} />
                )}
              </button>
            </div>

            {/* Auto-play Toggle */}
            <button
              type="button"
              onClick={() => { play('toggle'); setAutoSpeak(!autoSpeak); if (!autoSpeak) stopSpeaking(); }}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-3 rounded-kid-sm text-xs font-bold transition-all btn-squishy touch-kid touch-glow ${
                autoSpeak
                  ? 'bg-kid-ocean text-white shadow-md shadow-kid-ocean/20'
                  : 'bg-kid-bg text-kid-text-soft hover:bg-kid-ocean/10 border-2 border-kid-ocean/15'
              }`}
              aria-label={autoSpeak ? 'Mute auto-play' : 'Enable auto-play'}
              title={autoSpeak ? 'Auto-play: ON' : 'Auto-play: OFF'}
            >
              {autoSpeak ? <Volume2 className="w-5 h-5" strokeWidth={2.25} /> : <VolumeX className="w-5 h-5" strokeWidth={2.25} />}
              <span className="text-xs font-bold">{autoSpeak ? 'Sound' : 'Quiet'}</span>
            </button>
          </div>
          <p className="text-center text-kid-sm text-kid-text-soft/70 mt-2 font-bold">
            <Sparkles className="inline w-3 h-3 mr-1" />
            {isListening ? 'Listening... speak now!' : isSending ? 'Mindora is thinking...' : 'Type, speak, or tap the mic to ask!'}
          </p>
        </form>
      </footer>
    </div>
  );
}
